import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { IconEdit, IconMail, IconTrash } from '@tabler/icons-react'
import { Anchor, Button, Group, Stack, Text } from '@mantine/core'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { tableDefaultProps, tableDefaultAlertBannerProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { SpeciStatus, SpeciSummary } from '@loom-api-types'
import { Link } from 'react-router-dom'

interface HubSpeciTableProps {
  data: SpeciSummary[]
  renderKey?: string
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  deleteSpeci?: (id: string) => void
  reSendSpeci?: (id: string) => void
}

export const HubSpeciTable = ({
  data = [],
  renderKey = '',
  noDataMessage = 'No specis found for this station',
  isLoading,
  isError,
  deleteSpeci = () => {},
  reSendSpeci = () => {},
}: HubSpeciTableProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const navToEditSpeciPage = (speciId: string) => {
    navigate(`${pathname}/edit/${speciId}`)
  }

  const columns = useMemo<MRT_ColumnDef<SpeciSummary>[]>(
    () => [
      {
        id: 'author',
        accessorKey: 'author',
        header: 'Created',
        Cell: ({ cell, row }) => (
          <Stack gap='xs'>
            {row.original.originalSpeciCreated ? (
              <Text size='sm'>
                {formatDate(row.original.originalSpeciCreated, 'DD/MM/YYYY h:mm A')}
              </Text>
            ) : (
              <Text size='sm'>{formatDate(row.original.created, 'DD/MM/YYYY h:mm A')}</Text>
            )}
            <Text size='xs' c='dimmed'>
              {cell.getValue<string>()}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'originalSpeciSubmittedBy',
        accessorKey: 'originalSpeciSubmittedBy',
        header: 'Submitted',
        Cell: ({ cell, row }) => {
          const originalSubmittedBy = cell.getValue<string>()
          if (originalSubmittedBy) {
            return (
              <Stack gap='xs'>
                {cell.row.original.originalSpeciSubmittedAt && (
                  <Text size='sm'>
                    {formatDate(cell.row.original.originalSpeciSubmittedAt, 'DD/MM/YYYY h:mm A')}
                  </Text>
                )}
                <Text size='xs' c='dimmed'>
                  {cell.getValue<string>() ?? 'N/A'}
                </Text>
                {row.original.originalSpeciId && (
                  <Anchor
                    component={Link}
                    to={`${pathname}/view/${row.original.originalSpeciId}`}
                    size='sm'
                  >
                    View original speci
                  </Anchor>
                )}
              </Stack>
            )
          }
          return (
            <Stack gap='xs'>
              <Text size='sm'>N/A</Text>
              <Text size='xs' c='dimmed'>
                Speci created directly in Loom
              </Text>
            </Stack>
          )
        },
      },
      {
        id: 'sentToWoolStoreBy',
        accessorKey: 'sentToWoolStoreBy',
        header: 'Sent to Wool Store',
        Cell: ({ cell, row }) => (
          <Stack gap='xs'>
            <Text size='sm'>
              {formatDate(cell.row.original.sentToWoolStoreAt, 'DD/MM/YYYY h:mm A')}
            </Text>
            <Text size='xs' c='dimmed'>
              {cell.getValue<string>()}
            </Text>
            {row.original.status === SpeciStatus.Completed && (
              <Anchor component={Link} to={`${pathname}/view/${row.original.id}`} size='sm'>
                View completed speci
              </Anchor>
            )}
          </Stack>
        ),
      },
      {
        id: 'totalBales',
        accessorKey: 'totalBales',
        header: 'Bales',
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
      },
    ],
    [pathname]
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Speci table',
    },
    mantineTableBodyRowProps: ({ row }) => ({
      'data-testid': `speci-row-${row.original.id}`,
      ref: undefined, // Ensure compatibility with the expected type
    }),
    enableSorting: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) =>
      row.original.status === SpeciStatus.InProgress ? (
        <Group gap='xs'>
          <Button
            size='xs'
            variant='outline'
            onClick={() => navToEditSpeciPage(row.original.id)}
            leftSection={<IconEdit />}
          >
            Edit
          </Button>
          <Button
            size='xs'
            color='red'
            variant='outline'
            onClick={() => deleteSpeci(row.original.id)}
            leftSection={<IconTrash />}
          >
            Delete
          </Button>
        </Group>
      ) : (
        <Group>
          <Button
            size='xs'
            variant='outline'
            onClick={() => reSendSpeci(row.original.id)}
            leftSection={<IconMail />}
          >
            Resend
          </Button>
        </Group>
      ),
  })

  return <MantineReactTable table={table} key={renderKey} />
}
